import { Component } from '@theme/component';

const WORD_CLASS = 'heading-reveal__word';

/** @type {IntersectionObserver | undefined} */
let sharedObserver;

/**
 * Lazily creates one observer shared by every heading on the page.
 * @returns {IntersectionObserver}
 */
function getObserver() {
  sharedObserver ??= new IntersectionObserver(
    (entries, observer) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.setAttribute('revealed', '');
        observer.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -10% 0px' }
  );

  return sharedObserver;
}

/**
 * Splits heading text into words that rise into place when the heading scrolls into view.
 * Server-rendered text stays the source of truth; splitting is a progressive enhancement.
 *
 * @extends {Component}
 */
class HeadingReveal extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.setAttribute('revealed', '');
      return;
    }

    this.#split();
    if (!this.hasAttribute('revealed')) getObserver().observe(this);
  }

  updatedCallback() {
    super.updatedCallback();
    // Morphing toward server markup drops the split spans and the `revealed` attribute.
    this.#split();
    if (!this.hasAttribute('revealed')) getObserver().observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    sharedObserver?.unobserve(this);
  }

  /**
   * Wraps each word in a span, keeping inline markup, and exposes the original text once to
   * assistive technology. Headings with interactive content are left unsplit.
   */
  #split() {
    if (this.querySelector(`.${WORD_CLASS}`) || this.querySelector('a, button, input, select, textarea')) return;

    let index = 0;

    for (const container of this.children) {
      const label = container.textContent?.trim();
      if (!label) continue;

      const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
      /** @type {Text[]} */
      const textNodes = [];
      while (walker.nextNode()) textNodes.push(/** @type {Text} */ (walker.currentNode));

      for (const node of textNodes) {
        const fragment = document.createDocumentFragment();

        for (const part of node.data.split(/(\s+)/)) {
          if (!part) continue;
          if (/^\s+$/.test(part)) {
            fragment.append(part);
            continue;
          }

          const word = document.createElement('span');
          word.className = WORD_CLASS;
          word.setAttribute('aria-hidden', 'true');
          word.style.setProperty('--word-index', String(index++));
          word.textContent = part;
          fragment.append(word);
        }

        node.replaceWith(fragment);
      }

      const accessibleText = document.createElement('span');
      accessibleText.className = 'visually-hidden';
      accessibleText.textContent = label;
      container.prepend(accessibleText);
    }
  }
}

if (!customElements.get('heading-reveal')) {
  customElements.define('heading-reveal', HeadingReveal);
}
